import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../../environments/environment';

@Injectable()
export class ProjectPlanningService {
  constructor(private http: HttpClient) {
  }

  show(id) {
    return this.http.get<any>(`${environment.apiUrl}/project/planning/show/${id}`);
  }

  storeRole(param) {
    return this.http.post<any>(`${environment.apiUrl}/project/location-role`, param);
  }

  storePPCRole(param) {
    return this.http.post<any>(`${environment.apiUrl}/project/ppc/location-role`, param);
  }

  cloneRole(param) {
    return this.http.post<any>(`${environment.apiUrl}/project/location-role/clone`, param);
  }

  updateRole(param, id) {
    return this.http.put<any>(`${environment.apiUrl}/project/location-role/${id}`, param);
  }

  deleteRole(id) {
    return this.http.delete<any>(`${environment.apiUrl}/project/location-role/${id}`);
  }

  getRoleRequirementData(id) {
    return this.http.get<any>(`${environment.apiUrl}/project/role-requirement-data/${id}`);
  }

  storeShift(param) {
    return this.http.post<any>(`${environment.apiUrl}/project/role-shift`, param);
  }

  updateShift(param, id) {
    return this.http.put<any>(`${environment.apiUrl}/project/role-shift/${id}`, param);
  }

  calculateBreak(from, to, projectId: number | null = null) {
      // JS-1936: pass project_id so the backend can scope the break ladder to the client on
      // self-service / order projects. Without it the backend falls back to agency defaults.
      const scope = projectId ? `&project_id=${projectId}` : '';
      return this.http.get<any>(`${environment.apiUrl}/project/role-shift/break?from=${from}&to=${to}${scope}`);
  }

  deleteShift(id) {
    return this.http.delete<any>(`${environment.apiUrl}/project/role-shift/${id}`);
  }

  getEmployeeRequirement(id) {
    return this.http.get<any>(`${environment.apiUrl}/project/employee-requirement?project_id=${id}`);
  }

  updateEmplyeeRequirement(param, id) {
    return this.http.put<any>(`${environment.apiUrl}/project/employee-requirement/${id}`, param);
  }

  getCriteria(id) {
    return this.http.get<any>(`${environment.apiUrl}/project/criteria/${id}`);
  }

  getCriterias(type, clientId: number | null = null) {
    const scope = clientId ? `&client_id=${clientId}` : '';
    return this.http.get<any>(`${environment.apiUrl}/project/criteria?type=${type}${scope}`);
  }

  storeCriteria(param) {
    return this.http.post<any>(`${environment.apiUrl}/project/criteria`, param);
  }

  updateCriteria(id, param) {
    return this.http.put<any>(`${environment.apiUrl}/project/criteria/${id}`, param);
  }

  deleteCriteria(id, clientId: number | null = null) {
    const scope = clientId ? `?client_id=${clientId}` : '';
    return this.http.delete<any>(`${environment.apiUrl}/project/criteria/${id}${scope}`);
  }

  getAllCriteria(clientId: number | null = null) {
      const scope = clientId ? `?client_id=${clientId}` : '';
      return this.http.get<any>(`${environment.apiUrl}/project/criteria-all${scope}`);
  }

  getSalaryClass(id) {
    return this.http.get<any>(`${environment.apiUrl}/project/salary-class/${id}`);
  }

  getSalaryClasses() {
    return this.http.get<any>(`${environment.apiUrl}/project/salary-class`);
  }

  storeSalaryClass(param) {
    return this.http.post<any>(`${environment.apiUrl}/project/salary-class`, param);
  }

  updateSalaryClass(id, param) {
    return this.http.put<any>(`${environment.apiUrl}/project/salary-class/${id}`, param);
  }

  deleteSalaryClass(id) {
    return this.http.delete<any>(`${environment.apiUrl}/project/salary-class/${id}`);
  }

  getAllSalaryClass() {
      return this.http.get<any>(`${environment.apiUrl}/project/salary-class-all`);
  }

  getExpenses(roleId) {
    return this.http.get<any>(`${environment.apiUrl}/project/expense?role_id=${roleId}`);
  }

  saveExpenses(param) {
    return this.http.post<any>(`${environment.apiUrl}/project/expense-array`, param);
  }

  deleteExpense(id) {
    return this.http.delete<any>(`${environment.apiUrl}/project/expense/${id}`);
  }

  getWages(roleId) {
    return this.http.get<any>(`${environment.apiUrl}/project/wage?role_id=${roleId}`);
  }

  saveWageSurcharge(param) {
    return this.http.post<any>(`${environment.apiUrl}/project/wage/surcharge`, param);
  }

  updateWageSurcharge(id, param) {
    return this.http.put<any>(`${environment.apiUrl}/project/wage/surcharge/${id}`, param);
  }

  deleteWageSurcharge(id) {
    return this.http.delete<any>(`${environment.apiUrl}/project/wage/surcharge/${id}`);
  }

  saveWages(param) {
    return this.http.post<any>(`${environment.apiUrl}/project/wage-array`, param);
  }

  deleteWage(id) {
    return this.http.delete<any>(`${environment.apiUrl}/project/wage/${id}`);
  }

  getClothingRequirement(roleId) {
    return this.http.get<any>(`${environment.apiUrl}/project/clothing-requirement/${roleId}`);
  }

  saveClothingRequirement(param) {
    return this.http.post<any>(`${environment.apiUrl}/project/clothing-requirement`, param);
  }

  createClothingRequirementFromTemplate(param) {
    return this.http.post<any>(`${environment.apiUrl}/project/clothing-requirement-from-template`, param);
  }

  createClothingRequirementFromRoleTemplate(param) {
    return this.http.post<any>(`${environment.apiUrl}/project/clothing-requirement-from-role-template`, param);
  }

  deleteClothingRequirement(crId) {
    return this.http.delete<any>(`${environment.apiUrl}/project/clothing-requirement/${crId}`);
  }

  getClothingRequirementImages(roleId) {
    return this.http.get<any>(`${environment.apiUrl}/project/clothing-requirement-images/${roleId}`);
  }

  deleteClothingRequirementImage(criId) {
    return this.http.delete<any>(`${environment.apiUrl}/project/clothing-requirement-images/${criId}`);
  }

  loadTemplates(type, clientId: number | null = null, allClients = false) {
    // JS-1871: pass client_id when the caller is on a self-service surface so the modal
    // returns only that client's templates (see TemplateController::loadTemplates).
    // allClients=true is used by the agency-side self-service dashboard to fetch templates
    // across every client of the agency (server ignores the flag for client users).
    const clientParam = clientId ? `&client_id=${clientId}` : '';
    const allParam = allClients ? `&all_clients=1` : '';
    return this.http.get<any>(`${environment.apiUrl}/agency/template?type=${type}${clientParam}${allParam}`);
  }
  
  loadTemplate(id) {
    return this.http.get<any>(`${environment.apiUrl}/agency/template/${id}`);
  }

  getRoleTemplatesForClient(id) {
    return this.http.get<any>(`${environment.apiUrl}/agency/template/client-roles/${id}`);
  }

  saveTemplate(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/template`, param);
  }

  saveExistingTemplate(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/existing-template`, param);
  }

  checkIfTemplateExists(param) {
    return this.http.post<any>(`${environment.apiUrl}/agency/template-exists`, param);
  }

  removeLocationRoleFile(id, param, roleIds = null) {
    const name = '?name=' + encodeURIComponent(param);
    let ids = '';
    if (roleIds) {
      ids = '&role_ids=' + roleIds.join(',');
    }
    return this.http.delete<any>(`${environment.apiUrl}/project/role-file/${id}${name}${ids}`);
  }
  

  getClothRequirement(roleId) {
    return this.http.get<any>(`${environment.apiUrl}/project/clothing-requirement-array/${roleId}`);
  }

  saveClothRequirement(param) {
    return this.http.post<any>(`${environment.apiUrl}/project/clothing-requirement-array`, param);
  }

  getEmployeeRequirementsForLocation(id, params) {
    return this.http.post<any>(`${environment.apiUrl}/project/employee-requirements-for-location/${id}`, params);
  }

  getTariffGroups(projectId, params: any = {}) {
    return this.http.get<any>(`${environment.apiUrl}/project/get-tariff-groups/${projectId}`, { params });
  }

  // JS-1795 (template refactor): screen-2 load-from-template list for tariff groups.
  // params: { client_only?: boolean, department_only?: boolean, department_id?: number }
  getTariffGroupTemplatesForClient(projectId, params: any = {}) {
    return this.http.get<any>(`${environment.apiUrl}/agency/template/client-tariff-groups/${projectId}`, { params });
  }
}
